import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Cloud, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeatherZipConfig = ({ pageId }) => {
  const [zipCode, setZipCode] = useState('');
  const [weatherEnabled, setWeatherEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await axios.get(`${API}/admin/pages/${pageId}`);
        setZipCode(response.data.weather_zip_code || '');
        setWeatherEnabled(response.data.weather_enabled !== false);
      } catch (error) {
        console.error('Failed to load weather config:', error);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [pageId]);

  const handleToggleWeather = async (enabled) => {
    setWeatherEnabled(enabled);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { weather_enabled: enabled });
      toast.success(enabled ? 'Weather slide enabled' : 'Weather slide disabled');
    } catch (error) {
      setWeatherEnabled(!enabled);
      toast.error('Failed to update weather setting');
    }
  };

  const handleSaveZip = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { weather_zip_code: zipCode || '' });
      toast.success(zipCode ? `Weather zip code set to ${zipCode}` : 'Weather set to auto-detect');
    } catch (error) {
      toast.error('Failed to update weather zip code');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { weather_zip_code: '' });
      setZipCode('');
      toast.success('Weather set to auto-detect');
    } catch (error) {
      toast.error('Failed to clear weather zip code');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;

  return (
    <Card className="bg-slate-900 border-slate-800" data-testid="weather-zip-config">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Cloud className="w-5 h-5 text-cyan-400" /> Weather Slide
          </h3>
          <div className="flex items-center gap-3">
            <span className={`text-sm ${weatherEnabled ? 'text-cyan-400' : 'text-slate-500'}`}>
              {weatherEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              checked={weatherEnabled}
              onCheckedChange={handleToggleWeather}
              data-testid="weather-toggle"
            />
          </div>
        </div>
        
        {weatherEnabled && (
          <>
            <p className="text-slate-400 text-sm mb-4">
              Set a zip code for the weather slide. Leave empty to auto-detect based on display location.
            </p>
            <div className="flex gap-2">
              <Input
                type="text"
                value={zipCode}
                onChange={(e) => setZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                placeholder="e.g., 55401"
                className="bg-slate-950 border-slate-700 text-white max-w-[200px]"
                maxLength={5}
                data-testid="weather-zip-input"
              />
              <Button
                onClick={handleSaveZip}
                disabled={saving}
                className="bg-cyan-500 hover:bg-cyan-600"
                data-testid="save-weather-zip-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
              {zipCode && (
                <Button
                  onClick={handleClear}
                  disabled={saving}
                  variant="outline"
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="clear-weather-zip-btn"
                >
                  Clear
                </Button>
              )}
            </div>
            {zipCode && (
              <p className="text-cyan-400 text-xs mt-2">
                Currently set to: {zipCode}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WeatherZipConfig;
